import { useTranslation } from "react-i18next";
import { PenLine } from "lucide-react";
import { Button } from "@/components/ui";
import { ReceiptDisplay } from "./ReceiptDisplay";
import { ReceiptUploadButton } from "./ReceiptUploadButton";
import {
  showPartnerManualReceiptAmount,
  showPartnerReceiptSection,
  showPartnerReceiptUpload,
} from "./sellerBookingRules";
import type { BookingListRow } from "./bookingViewModel";

interface PartnerReceiptActionsProps {
  booking: BookingListRow;
  isProcessingReceipt: boolean;
  fileInputResetKey: number;
  onReceiptUpload: (file: File) => void;
  onManualAmountClick?: () => void;
  fullWidth?: boolean;
  showExistingReceipt?: boolean;
}

export function PartnerReceiptActions({
  booking,
  isProcessingReceipt,
  fileInputResetKey,
  onReceiptUpload,
  onManualAmountClick,
  fullWidth = false,
  showExistingReceipt = true,
}: PartnerReceiptActionsProps) {
  const { t } = useTranslation("bookings");

  if (!showPartnerReceiptSection(booking)) {
    return null;
  }

  const showManualReceiptAmount = showPartnerManualReceiptAmount(booking);
  const showReceiptUpload = showPartnerReceiptUpload(booking);

  if (
    showExistingReceipt &&
    booking.receiptTotalCents != null
  ) {
    return (
      <ReceiptDisplay
        receiptTotalCents={booking.receiptTotalCents}
        receiptUrl={booking.receiptUrl}
        compact
      />
    );
  }

  if (showManualReceiptAmount && onManualAmountClick) {
    return (
      <Button
        variant="primary"
        size="sm"
        className={fullWidth ? "w-full whitespace-nowrap" : "whitespace-nowrap"}
        leftIcon={<PenLine className="h-3.5 w-3.5" />}
        onClick={onManualAmountClick}
      >
        {t("receiptManualCta")}
      </Button>
    );
  }

  if (showReceiptUpload) {
    return (
      <ReceiptUploadButton
        bookingId={booking.id}
        isProcessing={isProcessingReceipt}
        fileInputResetKey={fileInputResetKey}
        fullWidth={fullWidth}
        visitAt={booking.visitAt}
        onFileSelect={(file) => onReceiptUpload(file)}
      />
    );
  }

  return null;
}
